import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  SCHEDULE_SLOT_KEYS,
  SCHEDULE_SLOT_LABELS,
  WEEKDAY_KEYS,
  WEEKDAY_SHORT_LABELS,
} from './constants';
import { getSlotChipSx } from './calendarChipColors';
import {
  buildRulesEnginePayload,
  formatCustomScheduleRuleHumanReadable,
  hydrateEditorDefaultsFromFormPayload,
} from './customScheduleRuleBuilder';
import { expandOutlookOccurrenceDates } from './outlookRecurrence';
import { getResolvedSlotKeysForDay } from './storage';

function newScheduleId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `cs-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function eachDateKeyInRange(start, end) {
  const keys = [];
  let cur = start.startOf('day');
  const last = end.startOf('day');
  if (!cur.isValid() || !last.isValid()) return keys;
  if (last.isBefore(cur)) return keys;
  while (!cur.isAfter(last, 'day')) {
    keys.push(cur.format('YYYY-MM-DD'));
    cur = cur.add(1, 'day');
  }
  return keys;
}

const OUTLOOK_DAY_CHIPS = [
  { dow: 0, label: 'Sun' },
  { dow: 1, label: 'Mon' },
  { dow: 2, label: 'Tue' },
  { dow: 3, label: 'Wed' },
  { dow: 4, label: 'Thu' },
  { dow: 5, label: 'Fri' },
  { dow: 6, label: 'Sat' },
];

const ORDINAL_OPTIONS = [
  { v: 1, label: 'first' },
  { v: 2, label: 'second' },
  { v: 3, label: 'third' },
  { v: 4, label: 'fourth' },
  { v: 5, label: 'last' },
];

const MONTH_OPTIONS = [
  { v: 1, label: 'January' },
  { v: 2, label: 'February' },
  { v: 3, label: 'March' },
  { v: 4, label: 'April' },
  { v: 5, label: 'May' },
  { v: 6, label: 'June' },
  { v: 7, label: 'July' },
  { v: 8, label: 'August' },
  { v: 9, label: 'September' },
  { v: 10, label: 'October' },
  { v: 11, label: 'November' },
  { v: 12, label: 'December' },
];

/**
 * Label in left column, controls in right column.
 * @param {{
 *   label: React.ReactNode,
 *   htmlFor?: string,
 *   labelId?: string,
 *   children: React.ReactNode,
 *   alignItems?: 'flex-start' | 'center',
 * }} props
 */
function FormRow({
  label,
  htmlFor,
  labelId,
  children,
  alignItems = 'flex-start',
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(100px, 36%) 1fr',
          sm: 'minmax(160px, 200px) 1fr',
          md: 'minmax(180px, 220px) 1fr',
        },
        columnGap: 2,
        rowGap: 0.5,
        alignItems,
      }}>
      <Typography
        id={labelId}
        component={htmlFor ? 'label' : 'div'}
        htmlFor={htmlFor}
        variant="body2"
        sx={{
          fontWeight: 500,
          pt: alignItems === 'center' ? 0 : { xs: 0.5, sm: 0.75 },
          color: 'text.primary',
        }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

/**
 * Page: always empty (user picks slots like a new form).
 * Dialog: cell slots from `initialSlotSelection` when passed, else same merge as Live Calendar.
 */
function deriveSlotSelection(
  initialSlotSelection,
  variant,
  liveInfo,
  selectedDate,
) {
  if (variant === 'page') {
    return [];
  }
  if (Array.isArray(initialSlotSelection)) {
    return SCHEDULE_SLOT_KEYS.filter(sk => initialSlotSelection.includes(sk));
  }
  if (selectedDate && dayjs.isDayjs(selectedDate)) {
    return getResolvedSlotKeysForDay(liveInfo, selectedDate);
  }
  return [];
}

/**
 * @param {{
 *   selectedDate?: import('dayjs').Dayjs | null,
 *   liveInfo: Record<string, { working?: boolean, slots?: string[] }>,
 *   initialSlotSelection?: string[],
 *   variant?: 'page' | 'dialog',
 *   customSchedulesList?: Array<{ id: string, name?: string, description?: string, working: boolean, form: Record<string, unknown> }>,
 *   onSave?: (
 *     next: Record<string, unknown> | { liveInfo: typeof liveInfo, customScheduleForm?: Record<string, unknown>, customSchedulesList?: unknown[] },
 *     variant: 'page' | 'dialog',
 *   ) => void,
 *   onCancel?: () => void,
 * }} props
 */
function CustomScheduling({
  selectedDate = null,
  liveInfo,
  customSchedulesList = [],
  initialSlotSelection,
  variant = 'page',
  onSave,
  onCancel,
  setPanel = () => {},
}) {
  const today = useMemo(() => dayjs().startOf('day'), []);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [recurring, setRecurring] = useState(false);
  /** @type {['single','range']} */
  const [durationShape, setDurationShape] = useState(
    /** @type {'single'|'range'} */ 'single',
  );
  const [singleDate, setSingleDate] = useState(() =>
    selectedDate && dayjs.isDayjs(selectedDate)
      ? selectedDate.startOf('day')
      : today,
  );
  const [rangeStart, setRangeStart] = useState(() => today);
  const [rangeEnd, setRangeEnd] = useState(() => today.add(1, 'week'));
  const [slotSelection, setSlotSelection] = useState(() =>
    deriveSlotSelection(initialSlotSelection, variant, liveInfo, selectedDate),
  );
  const [error, setError] = useState('');
  const [pageView, setPageView] = useState(
    variant === 'page' ? 'list' : 'form',
  );
  const [editingScheduleId, setEditingScheduleId] = useState(
    /** @type {string | null} */ null,
  );

  /** Microsoft Outlook–style recurrence when `recurring` */
  const [recPattern, setRecPattern] = useState(
    /** @type {'daily' | 'weekly' | 'monthly' | 'yearly'} */ 'weekly',
  );
  const [weeklyDays, setWeeklyDays] = useState(() => new Set([1, 2, 3, 4, 5]));
  const [monthlyMode, setMonthlyMode] = useState(
    /** @type {'dayOfMonth' | 'nthWeekday' | 'firstOrLastCalendarDay'} */ 'dayOfMonth',
  );
  const [monthlyDay, setMonthlyDay] = useState(1);
  const [monthlyNth, setMonthlyNth] = useState(1);
  const [monthlyWeekday, setMonthlyWeekday] = useState('MONDAY');
  const [monthlyFirstOrLast, setMonthlyFirstOrLast] = useState(
    /** @type {'first' | 'last'} */ 'first',
  );
  const [yearlyMode, setYearlyMode] = useState(
    /** @type {'date' | 'nthWeekday'} */ 'date',
  );
  const [yearlyMonth, setYearlyMonth] = useState(1);
  const [yearlyDay, setYearlyDay] = useState(1);
  const [yearlyNth, setYearlyNth] = useState(1);
  const [yearlyWeekday, setYearlyWeekday] = useState('MONDAY');
  const [rangeMode, setRangeMode] = useState(
    /** @type {'noEnd' | 'endBy'} */ 'endBy',
  );
  const [rangeEndBy, setRangeEndBy] = useState(() => today.add(1, 'year'));
  /** String mirrors for day-of-month inputs so clearing/editing does not snap to 1. */
  const [monthlyDayText, setMonthlyDayText] = useState('1');
  const [yearlyDayText, setYearlyDayText] = useState('1');

  const prevVariantRef = useRef(undefined);

  useEffect(() => {
    setMonthlyDayText(String(monthlyDay));
  }, [monthlyDay]);

  useEffect(() => {
    setYearlyDayText(String(yearlyDay));
  }, [yearlyDay]);

  const resetFormFields = useCallback(() => {
    setName('');
    setDescription('');
    setRecurring(false);
    setDurationShape('single');
    setSingleDate(
      selectedDate && dayjs.isDayjs(selectedDate)
        ? selectedDate.startOf('day')
        : today,
    );
    setRangeStart(today);
    setRangeEnd(today.add(1, 'week'));
    setSlotSelection(
      deriveSlotSelection(
        initialSlotSelection,
        variant,
        liveInfo,
        selectedDate,
      ),
    );
    setError('');
    setRecPattern('weekly');
    setWeeklyDays(new Set([1, 2, 3, 4, 5]));
    setMonthlyMode('dayOfMonth');
    setMonthlyDay(1);
    setMonthlyNth(1);
    setMonthlyWeekday('MONDAY');
    setMonthlyFirstOrLast('first');
    setYearlyMode('date');
    setYearlyMonth(1);
    setYearlyDay(1);
    setYearlyNth(1);
    setYearlyWeekday('MONDAY');
    setRangeMode('endBy');
    setRangeEndBy(today.add(1, 'year'));
  }, [today, variant, initialSlotSelection, liveInfo, selectedDate]);

  useEffect(() => {
    if (
      prevVariantRef.current !== undefined &&
      prevVariantRef.current !== variant
    ) {
      resetFormFields();
      setPageView(variant === 'page' ? 'list' : 'form');
      setEditingScheduleId(null);
    }
    prevVariantRef.current = variant;
  }, [variant, resetFormFields]);

  useEffect(() => {
    if (selectedDate && dayjs.isDayjs(selectedDate)) {
      setSingleDate(selectedDate.startOf('day'));
      setDurationShape('single');
    }
  }, [selectedDate]);

  const durationLabel = useMemo(() => {
    if (!recurring) {
      return durationShape === 'single' ? 'Single Day' : 'Multi Day';
    }
    return durationShape === 'single' ? 'Specific Date' : 'Specific Date Range';
  }, [recurring, durationShape]);

  const resolvedMonthlyDay = useMemo(() => {
    const t = monthlyDayText.trim();
    if (t === '') return monthlyDay;
    const v = parseInt(t, 10);
    return Number.isFinite(v) ? Math.min(31, Math.max(1, v)) : monthlyDay;
  }, [monthlyDayText, monthlyDay]);

  const resolvedYearlyDay = useMemo(() => {
    const t = yearlyDayText.trim();
    if (t === '') return yearlyDay;
    const v = parseInt(t, 10);
    return Number.isFinite(v) ? Math.min(31, Math.max(1, v)) : yearlyDay;
  }, [yearlyDayText, yearlyDay]);

  const toggleHorizontalSlot = key => {
    setSlotSelection(prev => {
      const set = new Set(prev);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return [...set];
    });
  };

  const toggleWeeklyDay = useCallback(dow => {
    setWeeklyDays(prev => {
      const n = new Set(prev);
      if (n.has(dow)) n.delete(dow);
      else n.add(dow);
      return n;
    });
  }, []);

  const outlookExpandParams = useMemo(
    () => ({
      seriesStart: singleDate,
      pattern: recPattern,
      dailyMode: 'interval',
      dailyEveryN: 1,
      weeklyEveryN: 1,
      weeklyDays: [...weeklyDays],
      monthlyMode,
      monthlyDay: resolvedMonthlyDay,
      monthlyNth,
      monthlyWeekday,
      monthlyFirstOrLast,
      yearlyMode,
      yearlyMonth,
      yearlyDay: resolvedYearlyDay,
      yearlyNth,
      yearlyWeekday,
      rangeMode,
      rangeEndBy,
    }),
    [
      singleDate,
      recPattern,
      weeklyDays,
      monthlyMode,
      resolvedMonthlyDay,
      monthlyNth,
      monthlyWeekday,
      monthlyFirstOrLast,
      yearlyMode,
      yearlyMonth,
      resolvedYearlyDay,
      yearlyNth,
      yearlyWeekday,
      rangeMode,
      rangeEndBy,
    ],
  );

  const affectedDateKeys = useMemo(() => {
    if (recurring) {
      return expandOutlookOccurrenceDates(outlookExpandParams);
    }
    if (durationShape === 'single') {
      return [singleDate.format('YYYY-MM-DD')];
    }
    return eachDateKeyInRange(rangeStart, rangeEnd);
  }, [
    recurring,
    outlookExpandParams,
    durationShape,
    singleDate,
    rangeStart,
    rangeEnd,
  ]);

  const validate = useCallback(() => {
    if (!name.trim()) return 'Name is required.';
    if (recurring) {
      if (recPattern === 'weekly' && weeklyDays.size === 0) {
        return 'Weekly recurrence: select at least one day of the week.';
      }
      if (rangeMode === 'endBy') {
        if (!rangeEndBy.isValid()) return 'Choose a valid end by date.';
        if (rangeEndBy.isBefore(singleDate, 'day')) {
          return 'End by date must be on or after the series start date.';
        }
      }
      const keys = expandOutlookOccurrenceDates(outlookExpandParams);
      if (!keys.length) {
        return 'No occurrences match this pattern and range. Adjust dates or pattern.';
      }
    }
    if (!recurring && durationShape === 'range') {
      if (!rangeStart.isValid() || !rangeEnd.isValid()) {
        return 'Choose a valid date range.';
      }
      if (rangeEnd.startOf('day').isBefore(rangeStart.startOf('day'))) {
        return 'End date must be on or after start date.';
      }
    }
    if (
      recurring &&
      recPattern === 'monthly' &&
      monthlyMode === 'dayOfMonth' &&
      (monthlyDayText.trim() === '' ||
        !Number.isFinite(parseInt(monthlyDayText.trim(), 10)))
    ) {
      return 'Enter a day of month from 1 to 31.';
    }
    if (
      recurring &&
      recPattern === 'yearly' &&
      yearlyMode === 'date' &&
      (yearlyDayText.trim() === '' ||
        !Number.isFinite(parseInt(yearlyDayText.trim(), 10)))
    ) {
      return 'Enter a day from 1 to 31 for the yearly date.';
    }
    if (!slotSelection.length) {
      return 'Select at least one time slot.';
    }
    return '';
  }, [
    name,
    recurring,
    recPattern,
    weeklyDays,
    rangeMode,
    rangeEndBy,
    singleDate,
    outlookExpandParams,
    durationShape,
    rangeStart,
    rangeEnd,
    slotSelection,
    monthlyMode,
    yearlyMode,
    monthlyDayText,
    yearlyDayText,
  ]);

  const buildFormPayload = useCallback(() => {
    const timeSlots = {
      mode: 'horizontal',
      slots: SCHEDULE_SLOT_KEYS.filter(k => slotSelection.includes(k)),
    };

    const outlookRecurrence = recurring
      ? {
          pattern: recPattern,
          daily: {
            mode: 'interval',
            everyNDays: 1,
          },
          weekly: {
            everyNWeeks: 1,
            daysOfWeek: [...weeklyDays].sort((a, b) => a - b),
          },
          monthly: {
            mode: monthlyMode,
            dayOfMonth: resolvedMonthlyDay,
            everyNMonths: 1,
            nth: monthlyNth,
            weekday: monthlyWeekday,
            firstOrLast: monthlyFirstOrLast,
          },
          yearly: {
            mode: yearlyMode,
            month: yearlyMonth,
            day: resolvedYearlyDay,
            nth: yearlyNth,
            weekday: yearlyWeekday,
          },
          range: {
            mode: rangeMode,
            endBy: rangeEndBy.format('YYYY-MM-DD'),
          },
          previewOccurrenceDates:
            expandOutlookOccurrenceDates(outlookExpandParams),
        }
      : null;

    let dates;
    if (recurring) {
      dates = { seriesStart: singleDate.format('YYYY-MM-DD') };
    } else if (durationShape === 'single') {
      dates = { single: singleDate.format('YYYY-MM-DD') };
    } else {
      dates = {
        start: rangeStart.format('YYYY-MM-DD'),
        end: rangeEnd.format('YYYY-MM-DD'),
      };
    }

    return {
      name: name.trim(),
      description: description.trim(),
      working: false,
      recurring,
      duration: recurring
        ? { shape: null, label: 'Outlook-style recurrence' }
        : {
            shape: durationShape,
            label: durationLabel,
          },
      dates,
      timeSlots,
      outlookRecurrence,
    };
  }, [
    name,
    description,
    recurring,
    recPattern,
    weeklyDays,
    monthlyMode,
    resolvedMonthlyDay,
    monthlyNth,
    monthlyWeekday,
    monthlyFirstOrLast,
    yearlyMode,
    yearlyMonth,
    resolvedYearlyDay,
    yearlyNth,
    yearlyWeekday,
    rangeMode,
    rangeEndBy,
    outlookExpandParams,
    durationShape,
    durationLabel,
    singleDate,
    rangeStart,
    rangeEnd,
    slotSelection,
  ]);

  const handleSave = () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    setError('');

    const nextLive = { ...liveInfo };
    const keys = affectedDateKeys;
    const slots = SCHEDULE_SLOT_KEYS.filter(k => slotSelection.includes(k));

    keys.forEach(k => {
      nextLive[k] =
        slots.length > 0 ? { working: false, slots } : { working: false };
    });

    const customScheduleForm = buildFormPayload();
    const payload = { liveInfo: nextLive, customScheduleForm };

    if (variant === 'page') {
      const id = editingScheduleId || newScheduleId();
      const record = {
        id,
        name: customScheduleForm.name,
        description: customScheduleForm.description,
        working: false,
        form: customScheduleForm,
      };
      const nextList = editingScheduleId
        ? customSchedulesList.map(r => (r.id === id ? record : r))
        : [...customSchedulesList, record];
      payload.customSchedulesList = nextList;
      payload.customScheduleRulesEngine = buildRulesEnginePayload(nextList);
      resetFormFields();
      setEditingScheduleId(null);
      setPageView('list');
    }

    onSave?.(payload, variant);
  };

  const handleDeleteSchedule = id => {
    const nextList = customSchedulesList.filter(r => r.id !== id);
    onSave?.(
      {
        customSchedulesList: nextList,
        customScheduleRulesEngine: buildRulesEnginePayload(nextList),
      },
      variant,
    );
  };

  const handleEditSchedule = id => {
    const rec = customSchedulesList.find(r => r.id === id);
    if (!rec?.form) return;
    const h = hydrateEditorDefaultsFromFormPayload(rec.form, today);
    if (!h) return;
    setName(h.name);
    setDescription(h.description);
    setRecurring(h.recurring);
    setDurationShape(h.durationShape);
    setSingleDate(h.singleDate);
    setRangeStart(h.rangeStart);
    setRangeEnd(h.rangeEnd);
    setSlotSelection(h.slotSelection);
    setRecPattern(h.recPattern);
    setWeeklyDays(h.weeklyDays);
    setMonthlyMode(h.monthlyMode);
    setMonthlyDay(h.monthlyDay);
    setMonthlyNth(h.monthlyNth);
    setMonthlyWeekday(h.monthlyWeekday);
    setMonthlyFirstOrLast(h.monthlyFirstOrLast);
    setYearlyMode(h.yearlyMode);
    setYearlyMonth(h.yearlyMonth);
    setYearlyDay(h.yearlyDay);
    setYearlyNth(h.yearlyNth);
    setYearlyWeekday(h.yearlyWeekday);
    setRangeMode(h.rangeMode);
    setRangeEndBy(h.rangeEndBy);
    setError('');
    setEditingScheduleId(id);
    setPageView('form');
  };

  const handleCreateSchedule = () => {
    resetFormFields();
    setEditingScheduleId(null);
    setError('');
    setPageView('form');
  };

  const handleFormDismiss = () => {
    if (variant === 'page') {
      resetFormFields();
      setEditingScheduleId(null);
      setPageView('list');
      return;
    }
    onCancel?.();
  };

  return (
    <Box>
      {variant === 'page' ? (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}>
            <Stack direction="column" spacing={1}>
              <Typography variant="h6" component="h2" sx={{ mb: 0 }}>
                Special Schedules
              </Typography>
              <Typography
                variant="caption"
                sx={{ mt: '0 !important', pt: 0, maxWidth: '80%' }}>
                Use a Special Schedule to set temporary or recurring
                date-specific changes to availability, including annual
                holidays, time off, or one-time availability changes, without
                changing the Default Schedule.
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setPanel('calendar');
                }}>
                Live Calendar
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setPanel('default');
                }}>
                Default Schedule
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {pageView === 'list' ? (
        <Box sx={{ textAlign: 'right', margin: 2 }}>
          <Button variant="outlined" onClick={handleCreateSchedule}>
            Create Special Schedule
          </Button>
        </Box>
      ) : null}

      {variant === 'page' && pageView === 'list' ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography
            color="primary"
            variant="subtitle1"
            sx={{ fontWeight: 500 }}>
            Special Schedules List
          </Typography>
          {customSchedulesList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No special schedules yet.
            </Typography>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small" aria-label="Special schedules">
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, minWidth: 100 }}>
                      &nbsp;
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 1 }}>
                      Schedule
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customSchedulesList.map(row => (
                    <TableRow key={row.id} hover>
                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        <Stack
                          direction="row"
                          spacing={0}
                          justifyContent="flex-end">
                          <IconButton
                            size="small"
                            aria-label="Edit schedule"
                            onClick={() => handleEditSchedule(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete schedule"
                            onClick={() => handleDeleteSchedule(row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 500, verticalAlign: 'top' }}
                        component="th"
                        scope="row">
                        {row.name || 'Untitled'}
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          color: 'text.secondary',
                          maxWidth: 220,
                        }}>
                        {row.description || '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          verticalAlign: 'top',
                          color: 'text.secondary',
                        }}>
                        <Typography variant="body2" component="span">
                          {formatCustomScheduleRuleHumanReadable(row)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : null}

      {(variant === 'dialog' ||
        (variant === 'page' && pageView === 'form')) && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: variant === 'dialog' ? 0 : 2 }}>
          {error ? (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}
          <Stack spacing={2.5} sx={{ maxWidth: 880 }}>
            <FormRow label="Name" htmlFor="custom-sched-name">
              <TextField
                id="custom-sched-name"
                value={name}
                onChange={e => setName(e.target.value)}
                size="small"
                fullWidth
                required
                placeholder="Enter a name"
                hiddenLabel
              />
            </FormRow>

            <FormRow
              label="Description"
              htmlFor="custom-sched-desc"
              alignItems="flex-start">
              <TextField
                id="custom-sched-desc"
                value={description}
                onChange={e => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                hiddenLabel
              />
            </FormRow>

            <FormRow label="Recurring" alignItems="center">
              <Switch
                checked={recurring}
                onChange={e => setRecurring(e.target.checked)}
                inputProps={{ 'aria-label': 'Recurring schedule' }}
              />
            </FormRow>

            {!recurring && (
              <FormRow label="Duration" labelId="custom-duration-label">
                <FormControl variant="standard" sx={{ width: '100%' }}>
                  <RadioGroup
                    row
                    aria-labelledby="custom-duration-label"
                    value={durationShape}
                    onChange={e =>
                      setDurationShape(
                        e.target.value === 'range' ? 'range' : 'single',
                      )
                    }>
                    <FormControlLabel
                      value="single"
                      control={<Radio size="small" />}
                      label="Single Day"
                    />
                    <FormControlLabel
                      value="range"
                      control={<Radio size="small" />}
                      label="Multi Day"
                    />
                  </RadioGroup>
                </FormControl>
              </FormRow>
            )}

            {recurring && (
              <>
                <FormRow label="Start" labelId="custom-outlook-start-label">
                  <DatePicker
                    value={singleDate}
                    disablePast
                    minDate={today}
                    onChange={v => {
                      if (v) setSingleDate(v.startOf('day'));
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        hiddenLabel: true,
                        placeholder: 'Series start',
                        'aria-labelledby': 'custom-outlook-start-label',
                      },
                    }}
                  />
                </FormRow>

                <FormRow
                  label="Recurrence pattern"
                  labelId="custom-outlook-pattern-label"
                  alignItems="flex-start">
                  <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                    <RadioGroup
                      aria-labelledby="custom-outlook-pattern-label"
                      value={recPattern}
                      onChange={e => setRecPattern(e.target.value)}>
                      <FormControlLabel
                        value="daily"
                        control={<Radio size="small" />}
                        label="Daily"
                      />
                      <FormControlLabel
                        value="weekly"
                        control={<Radio size="small" />}
                        label="Weekly"
                      />
                      <FormControlLabel
                        value="monthly"
                        control={<Radio size="small" />}
                        label="Monthly"
                      />
                      <FormControlLabel
                        value="yearly"
                        control={<Radio size="small" />}
                        label="Yearly"
                      />
                    </RadioGroup>

                    {recPattern === 'weekly' && (
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Every week on:
                        </Typography>
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          useFlexGap
                          gap={0.5}>
                          {OUTLOOK_DAY_CHIPS.map(({ dow, label }) => (
                            <FormControlLabel
                              key={dow}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={weeklyDays.has(dow)}
                                  onChange={() => toggleWeeklyDay(dow)}
                                />
                              }
                              label={label}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    {recPattern === 'monthly' && (
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <RadioGroup
                          value={monthlyMode}
                          onChange={e => setMonthlyMode(e.target.value)}>
                          <FormControlLabel
                            value="dayOfMonth"
                            control={<Radio size="small" />}
                            label={
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap">
                                <Typography variant="body2">Day</Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 1, max: 31 }}
                                  value={monthlyDayText}
                                  onChange={ev => {
                                    const next = ev.target.value;
                                    setMonthlyDayText(next);
                                    const t = next.trim();
                                    if (t !== '') {
                                      const v = parseInt(t, 10);
                                      if (Number.isFinite(v)) {
                                        setMonthlyDay(
                                          Math.min(31, Math.max(1, v)),
                                        );
                                      }
                                    }
                                  }}
                                  onBlur={() => {
                                    const t = monthlyDayText.trim();
                                    const v = parseInt(t, 10);
                                    const c = Number.isFinite(v)
                                      ? Math.min(31, Math.max(1, v))
                                      : 1;
                                    setMonthlyDay(c);
                                    setMonthlyDayText(String(c));
                                  }}
                                  sx={{ width: 64 }}
                                  disabled={monthlyMode !== 'dayOfMonth'}
                                />
                                <Typography variant="body2">
                                  of every month
                                </Typography>
                              </Stack>
                            }
                          />
                          <FormControlLabel
                            value="nthWeekday"
                            control={<Radio size="small" />}
                            label={
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{ py: 0.5 }}>
                                <Typography variant="body2">The</Typography>
                                <Select
                                  size="small"
                                  value={monthlyNth}
                                  disabled={monthlyMode !== 'nthWeekday'}
                                  onChange={e =>
                                    setMonthlyNth(Number(e.target.value))
                                  }
                                  sx={{ minWidth: 100 }}>
                                  {ORDINAL_OPTIONS.map(o => (
                                    <MenuItem key={o.v} value={o.v}>
                                      {o.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 120 }}>
                                  <Select
                                    value={monthlyWeekday}
                                    disabled={monthlyMode !== 'nthWeekday'}
                                    onChange={e =>
                                      setMonthlyWeekday(e.target.value)
                                    }>
                                    {WEEKDAY_KEYS.map(wk => (
                                      <MenuItem key={wk} value={wk}>
                                        {WEEKDAY_SHORT_LABELS[wk]}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Typography variant="body2">
                                  of every month
                                </Typography>
                              </Stack>
                            }
                          />
                          <FormControlLabel
                            value="firstOrLastCalendarDay"
                            control={<Radio size="small" />}
                            label={
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap">
                                <Typography variant="body2">Day</Typography>
                                <Select
                                  size="small"
                                  value={monthlyFirstOrLast}
                                  disabled={
                                    monthlyMode !== 'firstOrLastCalendarDay'
                                  }
                                  onChange={e =>
                                    setMonthlyFirstOrLast(
                                      /** @type {'first'|'last'} */ e.target
                                        .value,
                                    )
                                  }
                                  sx={{ minWidth: 100 }}>
                                  <MenuItem value="first">First</MenuItem>
                                  <MenuItem value="last">Last</MenuItem>
                                </Select>
                                <Typography variant="body2">
                                  of every month
                                </Typography>
                              </Stack>
                            }
                          />
                        </RadioGroup>
                      </Stack>
                    )}

                    {recPattern === 'yearly' && (
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <RadioGroup
                          value={yearlyMode}
                          onChange={e => setYearlyMode(e.target.value)}>
                          <FormControlLabel
                            value="date"
                            control={<Radio size="small" />}
                            label={
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap">
                                <Typography variant="body2">Every</Typography>
                                <Select
                                  size="small"
                                  value={yearlyMonth}
                                  disabled={yearlyMode !== 'date'}
                                  onChange={e =>
                                    setYearlyMonth(Number(e.target.value))
                                  }
                                  sx={{ minWidth: 120 }}>
                                  {MONTH_OPTIONS.map(m => (
                                    <MenuItem key={m.v} value={m.v}>
                                      {m.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <TextField
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 1, max: 31 }}
                                  value={yearlyDayText}
                                  onChange={ev => {
                                    const next = ev.target.value;
                                    setYearlyDayText(next);
                                    const t = next.trim();
                                    if (t !== '') {
                                      const v = parseInt(t, 10);
                                      if (Number.isFinite(v)) {
                                        setYearlyDay(
                                          Math.min(31, Math.max(1, v)),
                                        );
                                      }
                                    }
                                  }}
                                  onBlur={() => {
                                    const t = yearlyDayText.trim();
                                    const v = parseInt(t, 10);
                                    const c = Number.isFinite(v)
                                      ? Math.min(31, Math.max(1, v))
                                      : 1;
                                    setYearlyDay(c);
                                    setYearlyDayText(String(c));
                                  }}
                                  sx={{ width: 64 }}
                                  disabled={yearlyMode !== 'date'}
                                />
                              </Stack>
                            }
                          />
                          <FormControlLabel
                            value="nthWeekday"
                            control={<Radio size="small" />}
                            label={
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap">
                                <Typography variant="body2">The</Typography>
                                <Select
                                  size="small"
                                  value={yearlyNth}
                                  disabled={yearlyMode !== 'nthWeekday'}
                                  onChange={e =>
                                    setYearlyNth(Number(e.target.value))
                                  }
                                  sx={{ minWidth: 100 }}>
                                  {ORDINAL_OPTIONS.map(o => (
                                    <MenuItem key={o.v} value={o.v}>
                                      {o.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 120 }}>
                                  <Select
                                    value={yearlyWeekday}
                                    disabled={yearlyMode !== 'nthWeekday'}
                                    onChange={e =>
                                      setYearlyWeekday(e.target.value)
                                    }>
                                    {WEEKDAY_KEYS.map(wk => (
                                      <MenuItem key={wk} value={wk}>
                                        {WEEKDAY_SHORT_LABELS[wk]}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Typography variant="body2">of</Typography>
                                <Select
                                  size="small"
                                  value={yearlyMonth}
                                  disabled={yearlyMode !== 'nthWeekday'}
                                  onChange={e =>
                                    setYearlyMonth(Number(e.target.value))
                                  }
                                  sx={{ minWidth: 120 }}>
                                  {MONTH_OPTIONS.map(m => (
                                    <MenuItem key={m.v} value={m.v}>
                                      {m.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Stack>
                            }
                          />
                        </RadioGroup>
                      </Stack>
                    )}
                  </Paper>
                </FormRow>

                <FormRow
                  label="End Date"
                  labelId="custom-outlook-range-label"
                  alignItems="flex-start">
                  <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Start:{' '}
                      <strong>{singleDate.format('dddd, MMMM D, YYYY')}</strong>
                    </Typography>
                    <RadioGroup
                      aria-labelledby="custom-outlook-range-label"
                      value={rangeMode}
                      onChange={e => setRangeMode(e.target.value)}>
                      <FormControlLabel
                        value="noEnd"
                        control={<Radio size="small" />}
                        label="No end date"
                      />
                      <FormControlLabel
                        value="endBy"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ sm: 'center' }}>
                            <Typography variant="body2">End by</Typography>
                            <DatePicker
                              value={rangeEndBy}
                              minDate={singleDate}
                              onChange={v => {
                                if (v) setRangeEndBy(v.startOf('day'));
                              }}
                              disabled={rangeMode !== 'endBy'}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  sx: { maxWidth: 220 },
                                },
                              }}
                            />
                          </Stack>
                        }
                      />
                    </RadioGroup>
                  </Paper>
                </FormRow>
              </>
            )}

            {!recurring && durationShape === 'single' && (
              <FormRow label="Date">
                <DatePicker
                  value={singleDate}
                  disablePast
                  minDate={today}
                  onChange={v => {
                    if (v) setSingleDate(v.startOf('day'));
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      hiddenLabel: true,
                      placeholder: 'Select date',
                    },
                  }}
                />
              </FormRow>
            )}

            {!recurring && durationShape === 'range' && (
              <FormRow label="Date range">
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ width: '100%' }}>
                  <DatePicker
                    label="Start"
                    value={rangeStart}
                    disablePast
                    minDate={today}
                    onChange={v => {
                      if (v) {
                        const s = v.startOf('day');
                        setRangeStart(s);
                        if (rangeEnd.isBefore(s, 'day')) {
                          setRangeEnd(s);
                        }
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: { flex: 1 },
                      },
                    }}
                  />
                  <DatePicker
                    label="End"
                    value={rangeEnd}
                    minDate={
                      rangeStart.isBefore(today, 'day') ? today : rangeStart
                    }
                    onChange={v => {
                      if (v) setRangeEnd(v.startOf('day'));
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: { flex: 1 },
                      },
                    }}
                  />
                </Stack>
              </FormRow>
            )}

            <FormRow label="Time slots" alignItems="flex-start">
              <Stack direction="column" spacing={0.2} sx={{ width: '100%' }}>
                {SCHEDULE_SLOT_KEYS.map(key => {
                  const checked = slotSelection.includes(key);
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      alignItems="center"
                      spacing={0.1}
                      component="label"
                      sx={{
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleHorizontalSlot(key)}
                        inputProps={{
                          'aria-label': `${SCHEDULE_SLOT_LABELS[key]} time slot`,
                        }}
                      />
                      <Chip
                        label={
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'left',
                              color: 'inherit',
                              ml: 1,
                              mt: 0.1,
                            }}>
                            {SCHEDULE_SLOT_LABELS[key]}
                          </Typography>
                        }
                        size="small"
                        variant="filled"
                        sx={{
                          ...getSlotChipSx(key),
                          minWidth: '100px',
                        }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </FormRow>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mt: 1,
              }}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleSave}>
                  Save
                </Button>
                <Button variant="outlined" onClick={handleFormDismiss}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

export default CustomScheduling;
